import { createContext, useCallback, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

const UiContext = createContext({});

export const UiContextProvider = ({ children }) => {
  const [toastQueue, setToastQueue] = useState([]);
  const [clipboard, setClipboard] = useState();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'), { noSsr: true });
  const isSm = useMediaQuery(theme.breakpoints.only('sm'), { noSsr: true });
  const isMd = useMediaQuery(theme.breakpoints.only('md'), { noSsr: true });
  const isLg = useMediaQuery(theme.breakpoints.only('lg'), { noSsr: true });
  const isXl = useMediaQuery(theme.breakpoints.only('xl'), { noSsr: true });
  const isXxl = useMediaQuery(theme.breakpoints.only('xxl'), { noSsr: true });
  const xsPlus = true;
  const smPlus = !isXs;
  const mdPlus = smPlus && !isSm;
  const lgPlus = mdPlus && !isMd;
  const xlPlus = lgPlus && !isLg;
  const xxlPlus = xlPlus && !isXl;
  const breakpoint =
    (isXs && 'xs') ||
    (isSm && 'sm') ||
    (isMd && 'md') ||
    (isLg && 'lg') ||
    (isXl && 'xl') ||
    (isXxl && 'xxl');

  const queue = useCallback(
    (type, message, tooltip) => setToastQueue(p => [...p, { type, message, tooltip }]),
    [setToastQueue],
  );
  const toastError = useCallback(
    (message, tooltip) => queue('error', message || 'An unknown error has occurred', tooltip),
    [queue],
  );
  const toastWarning = useCallback(
    (message, tooltip) => queue('warning', message, tooltip),
    [queue],
  );
  const toastInfo = useCallback((message, tooltip) => queue('info', message, tooltip), [queue]);
  const toastSuccess = useCallback(
    (message, tooltip) => queue('success', message, tooltip),
    [queue],
  );

  function copy(data, dataType) {
    setClipboard({ data, type: dataType });
  }

  function paste() {
    const data = clipboard?.data;
    setClipboard(undefined); // unconventional, but otherwise they see paste icons everywhere forever
    return data;
  }

  const [autoScroll, setAutoScroll] = useState(false);

  return (
    // prettier-ignore
    <UiContext.Provider
      value={{
        isXs, isSm, isMd, isLg, isXl, isXxl, xsPlus, smPlus, mdPlus, lgPlus, xlPlus, xxlPlus, breakpoint,
        toastQueue, setToastQueue, toastSuccess, toastInfo, toastWarning, toastError,
        copy, paste, clipboardType: clipboard?.type,
        autoScroll, setAutoScroll,
      }}
    >
      {children}
    </UiContext.Provider>
  );
};

export default UiContext;
